import React, { useRef, useEffect, useState } from "react";
import { FileWithPath } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import ColorJarLogo from "./ColorJarLogo";
import "./WelcomeScreen.css";

interface WelcomeScreenProps {
  onImageUpload: (acceptedFiles: FileWithPath[]) => void;
  onImageUrlLoad: (url: string) => Promise<void>;
  isImageLoaded: boolean;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  onImageUpload,
  onImageUrlLoad,
  isImageLoaded,
}) => {
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isLoadingUrl, setIsLoadingUrl] = useState(false);
  const [urlError, setUrlError] = useState("");
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const preventDefault = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e: DragEvent) => {
      preventDefault(e);
      setIsDragging(true);
    };

    const handleDragLeave = (e: DragEvent) => {
      preventDefault(e);
      if (e.target === dropzoneRef.current) {
        setIsDragging(false);
      }
    };

    const handleDrop = (e: DragEvent) => {
      preventDefault(e);
      setIsDragging(false);
      if (isImageUploaded || isImageLoaded) return;

      if (e.dataTransfer?.files) {
        const files = Array.from(e.dataTransfer.files) as FileWithPath[];
        onImageUpload(files);
        setIsImageUploaded(true);
      }
    };

    const element = dropzoneRef.current;
    if (element) {
      element.addEventListener("dragenter", handleDragEnter);
      element.addEventListener("dragover", preventDefault);
      element.addEventListener("dragleave", handleDragLeave);
      element.addEventListener("drop", handleDrop);
    }

    return () => {
      if (element) {
        element.removeEventListener("dragenter", handleDragEnter);
        element.removeEventListener("dragover", preventDefault);
        element.removeEventListener("dragleave", handleDragLeave);
        element.removeEventListener("drop", handleDrop);
      }
    };
  }, [onImageUpload, isImageUploaded, isImageLoaded]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && !isImageUploaded && !isImageLoaded) {
      const files = Array.from(event.target.files) as FileWithPath[];
      onImageUpload(files);
      setIsImageUploaded(true);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!imageUrl.trim()) return;

    // Basic URL validation
    try {
      new URL(imageUrl);
    } catch {
      setUrlError("Please enter a valid URL");
      return;
    }

    setIsLoadingUrl(true);
    setUrlError("");

    try {
      await onImageUrlLoad(imageUrl);
      setIsImageUploaded(true);
    } catch (error) {
      setUrlError(error instanceof Error ? error.message : "Failed to load image");
    } finally {
      setIsLoadingUrl(false);
    }
  };

  if (isImageUploaded || isImageLoaded) {
    return null;
  }

  return (
    <div className="welcome-container">
      {/* Floating background orbs */}
      <div className="welcome-orbs">
        <div className="welcome-orb orb-1"></div>
        <div className="welcome-orb orb-2"></div>
        <div className="welcome-orb orb-3"></div>
      </div>

      <div className="welcome-header animate-in" style={{ animationDelay: '0.1s' }}>
        <div className="logo-container">
          <ColorJarLogo />
          <h1 className="app-title">ColorJar</h1>
        </div>
        <p className="app-subtitle">Create beautiful color palettes from any image</p>
      </div>

      <div
        ref={dropzoneRef}
        className={`upload-area animate-in ${isDragging ? 'dragging' : ''}`}
        style={{ animationDelay: '0.3s' }}
        onClick={handleButtonClick}
      >
        <div className="upload-content">
          <CloudUploadIcon className="upload-icon" />
          <h2 className="upload-title">Upload Image</h2>
          <p className="upload-text">or drop it right here</p>
          <button className="browse-button">Browse Files</button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>

      <div className="url-section animate-in" style={{ animationDelay: '0.4s' }}>
        <div className="url-divider">
          <span>or paste image URL</span>
        </div>
        <form onSubmit={handleUrlSubmit} className="url-form">
          <div className="url-input-wrapper">
            <LinkIcon className="url-input-icon" />
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => {
                setImageUrl(e.target.value);
                setUrlError("");
              }}
              placeholder="https://example.com/image.jpg"
              className="url-input"
              disabled={isLoadingUrl}
            />
          </div>
          <button
            type="submit"
            className="url-submit-button"
            disabled={isLoadingUrl || !imageUrl.trim()}
          >
            {isLoadingUrl ? "Loading..." : "Load"}
          </button>
        </form>
        {urlError && <p className="url-error">{urlError}</p>}
      </div>

      <div className="features-section animate-in" style={{ animationDelay: '0.5s' }}>
        <h3>How it works</h3>
        <div className="features-grid">
          <div className="feature-item" style={{ animationDelay: '0.6s' }}>
            <div className="feature-number">1</div>
            <p>Upload any image</p>
          </div>
          <div className="feature-item" style={{ animationDelay: '0.7s' }}>
            <div className="feature-number">2</div>
            <p>Click to pick colors</p>
          </div>
          <div className="feature-item" style={{ animationDelay: '0.8s' }}>
            <div className="feature-number">3</div>
            <p>Export your palette</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;